import { Activity, RefObject, useRef } from 'react';
import { useOnClickOutside } from 'usehooks-ts';

import {
  useCloseLmmSidepanel,
  useLmmSidepanelMaterieel,
  useLmmSidepanelState,
  useRestoreSidepanelLmmVoorraad,
} from '@/apps/lmm/state/selectors/useLmmSidepanelStore';
import SidepanelClose from '@/components/UI/Sidepanel/SidepanelClose';
import { DefectGroupedByEenheid } from '@/domain/defecten/defectEenheid';

import useLmmStore from '../../state/useLmmStore';
import { getLmmTijdstipTitel, isLmmTijdstipNu } from '../../utils/lmmTijdstippen';
import { stellenUitVoorraad } from '../../utils/lmmVoorraadStellen';
import LmmOverTekortTooltip from '../LmmTableMaterieel/LmmOverTekortTooltip';

import LmmMaterieelPanel from './LmmMaterieelPanel';
import LmmVerwachtStellenSection from './LmmVerwachtStellenSection';

import styles from './LmmSidepanel.module.css';
export default function LmmSidepanel() {
  const { show, view, lmmVoorraad, lmmTijdstip, kanTerugNaarVoorraad } = useLmmSidepanelState();
  const closeSidepanel = useCloseLmmSidepanel();
  const restoreSidepanelLmmVoorraad = useRestoreSidepanelLmmVoorraad();
  const lmmMaterieel = useLmmSidepanelMaterieel();
  const defecten = useLmmStore((state) => (show && lmmMaterieel ? state.defecten : []));
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref as RefObject<HTMLElement>, () => {
    setTimeout(closeSidepanel, 200);
  });

  const isNu = !lmmTijdstip || isLmmTijdstipNu(lmmTijdstip.label);

  /**
   * Deze kolom kan kan op 2 manieren worden geinterpreteerd:
   * Als het nu moment is dan zijn dit alle niet inzetbare stellen;
   * Als het een ander tijdstip is dan zijn dit alle verwacht gereed stellen;
   */
  const eenhedenNietInzetbaar: DefectGroupedByEenheid[] = stellenUitVoorraad(
    defecten,
    lmmVoorraad?.nietInzetbaar.defecten.ids,
  );

  const eenhedenVerwachtGereed: DefectGroupedByEenheid[] = stellenUitVoorraad(
    defecten,
    lmmVoorraad?.verwachtGereed.defecten.ids,
  );

  return (
    <Activity mode={show && lmmVoorraad ? 'visible' : 'hidden'}>
      <div ref={ref} className={styles.sidepanel}>
        {view === 'materieel' ? (
          <LmmMaterieelPanel
            onClose={closeSidepanel}
            onBack={kanTerugNaarVoorraad ? restoreSidepanelLmmVoorraad : undefined}
          />
        ) : (
          <>
            <header className={styles.sidepanelHeader}>
              <div className={styles.sidepanelHeaderTitleDiv}>
                <h3>{lmmVoorraad?.materieel}</h3>
                <SidepanelClose close={closeSidepanel} />
              </div>
              <div className={styles.sidepanelHeaderTotal}>
                <span className={styles.sidepanelHeaderTotalLabel}>
                  {isNu ? 'Over/tekort nu:' : `Over/tekort om ${getLmmTijdstipTitel(lmmTijdstip)}:`}
                </span>
                {lmmVoorraad && <LmmOverTekortTooltip voorraad={lmmVoorraad} tijdstip={lmmTijdstip} compact />}
              </div>
            </header>
            <div className={styles.sidepanelContent}>
              {isNu ? (
                <LmmVerwachtStellenSection defecten={eenhedenNietInzetbaar} title='Niet inzetbaar' />
              ) : (
                <>
                  <LmmVerwachtStellenSection defecten={eenhedenNietInzetbaar} title='Verwacht niet inzetbaar' />
                  <LmmVerwachtStellenSection
                    isVerwachtGereed
                    defecten={eenhedenVerwachtGereed}
                    title='Verwacht gereed'
                  />
                </>
              )}
            </div>
          </>
        )}
      </div>
    </Activity>
  );
}
